import '@progress/kendo-drawing';
import { ROTATED, ABSOLUTE, TRANSFORMED, ITEMROTATE, WIDTH, HEIGHT, X, Y, ITEMBOUNDSCHANGE } from '../constants.js';
import { deepExtend } from '../utils/deep-extend.js';
import { contains, isString, defined, remove, isFunction, isDefined, isUndefined } from '../utils/functions.js';
import { randomId } from '../math/randomId.js';
import '../math/Graph.js';
import { Intersect } from '../math/Intersect.js';
import { Rect, RectAlign, Point } from '../math/Shapes.js';
import '../svg/Rotation.js';
import { diffNumericOptions } from '../svg/diffNumericOptions.js';
import { Circle } from '../svg/Circle.js';
import { Image } from '../svg/Image.js';
import { Path } from '../svg/Path.js';
import { Rectangle } from '../svg/Rectangle.js';
import { TextBlock } from '../svg/TextBlock.js';
import { filterShapeDataItem, closestConnector, translateToOrigin } from './utils.js';
import { RotateUnit } from '../services/RotateUnit.js';
import { Connector } from './Connector.js';
import { shapeDefaults } from './shapeDefaults.js';
import { DiagramElement } from './DiagramElement.js';

class Shape extends DiagramElement {
    constructor(options, diagram) {
        super(options);
        this.options = deepExtend({}, this.options, shapeDefaults(), options);
        this.diagram = diagram;
        this.updateOptionsFromModel();
        options = this.options;
        this.connectors = [];
        this.type = options.type;
        this.createShapeVisual();
        this.updateBounds();
        this.content(this.content());
        this._createConnectors();
    }
    _setOptionsFromModel(model) {
        const modelOptions = filterShapeDataItem(model || this.dataItem);
        this.options = deepExtend({}, this.options, modelOptions);
        this.redrawVisual();
    }
    updateOptionsFromModel(model, field) {
        if (this.diagram && this.diagram._isEditable) {
            const modelOptions = filterShapeDataItem(model || this.dataItem);
            if (model && field) {
                if (!contains(['x', 'y', 'width', 'height'], field)) {
                    if (this.options.visual) {
                        this._redrawVisual();
                    }
                    else if (modelOptions.type) {
                        this.options = deepExtend({}, this.options, modelOptions);
                        this._redrawVisual();
                    }
                    if (this.options.content) {
                        this._template();
                        this.content(this.options.content);
                    }
                }
                else {
                    const bounds = this.bounds();
                    bounds[field] = model[field];
                    this.bounds(bounds);
                }
            }
            else {
                this.options = deepExtend({}, this.options, modelOptions);
            }
        }
    }
    _redrawVisual() {
        this.visual.clear();
        this._contentVisual = null;
        this.options.dataItem = this.dataItem;
        this.createShapeVisual();
        this.updateBounds();
    }
    redrawVisual() {
        this._redrawVisual();
        if (this.options.content) {
            this._template();
            this.content(this.options.content);
        }
    }
    updateModel(syncChanges) {
        const diagram = this.diagram;
        if (diagram && diagram._isEditable) {
            diagram.updateShapeModel(this, syncChanges);
        }
    }
    updateBounds() {
        const bounds = this.visual._measure(true);
        const options = this.options;
        this.bounds(new Rect(options.x, options.y, bounds.width, bounds.height));
        this._rotate();
        this._alignContent();
    }
    content(content) {
        const result = this._content(content);
        this._alignContent();
        return result;
    }
    _alignContent() {
        const contentOptions = this.options.content || {};
        const contentVisual = this._contentVisual;
        if (contentVisual && contentOptions.align) {
            const containerRect = this.visual._measure();
            const aligner = new RectAlign(containerRect);
            const contentBounds = contentVisual.drawingElement.bbox(null);
            const contentRect = new Rect(0, 0, contentBounds.width(), contentBounds.height());
            const alignedBounds = aligner.align(contentRect, contentOptions.align);
            contentVisual.position(alignedBounds.topLeft());
        }
    }
    _createConnectors() {
        const options = this.options, length = options.connectors.length, connectorDefaults = options.connectorDefaults;
        let connector, i;
        for (i = 0; i < length; i++) {
            connector = new Connector(this, deepExtend({}, connectorDefaults, options.connectors[i]));
            this.connectors.push(connector);
        }
    }
    bounds(value) {
        let bounds;
        if (value) {
            if (isString(value)) {
                switch (value) {
                    case TRANSFORMED: {
                        bounds = this._transformedBounds();
                        break;
                    }
                    case ABSOLUTE: {
                        bounds = this._transformedBounds();
                        const pan = this.diagram._pan;
                        bounds.x += pan.x;
                        bounds.y += pan.y;
                        break;
                    }
                    case ROTATED: {
                        bounds = this._rotatedBounds();
                        break;
                    }
                    default: {
                        bounds = this._bounds;
                        break;
                    }
                }
            }
            else {
                this._setBounds(value);
                this._triggerBoundsChange();
                if (!(this.diagram && this.diagram._layouting)) {
                    this.refreshConnections();
                }
            }
        }
        else {
            bounds = this._bounds;
        }
        return bounds;
    }
    _setBounds(rect) {
        const options = this.options;
        const topLeft = rect.topLeft();
        const x = options.x = topLeft.x;
        const y = options.y = topLeft.y;
        const width = options.width = Math.max(rect.width, options.minWidth);
        const height = options.height = Math.max(rect.height, options.minHeight);
        this._bounds = new Rect(x, y, width, height);
        this.visual.redraw({
            x: x,
            y: y,
            width: width,
            height: height
        });
    }
    position(point) {
        if (point) {
            this.bounds(new Rect(point.x, point.y, this._bounds.width, this._bounds.height));
        }
        else {
            return this._bounds.topLeft();
        }
    }
    /**
     * Returns a clone of this shape.
     *
     * @returns {Shape}
     */
    clone() {
        const json = this.serialize();
        json.options.id = randomId();
        if (this.diagram && this.diagram._isEditable && defined(this.dataItem)) {
            json.options.dataItem = this.diagram.options.cloneDataItem(this.dataItem);
        }
        return new Shape(json.options, undefined);
    }
    select(value) {
        const diagram = this.diagram;
        let selected, deselected;
        if (isUndefined(value)) {
            value = true;
        }
        if (this._canSelect()) {
            if (this.isSelected !== value) {
                selected = [];
                deselected = [];
                this.isSelected = value;
                if (this.isSelected) {
                    diagram._selectedItems.push(this);
                    selected.push(this);
                }
                else {
                    remove(diagram._selectedItems, this);
                    deselected.push(this);
                }
                if (!diagram._internalSelection) {
                    diagram._selectionChanged(selected, deselected);
                }
                return true;
            }
        }
    }
    rotate(angle, center, undoable) {
        const rotate = this.visual.rotate();
        if (angle !== undefined) {
            if (undoable !== false && this.diagram && this.diagram.undoRedoService && angle !== rotate.angle) {
                this.diagram.undoRedoService.add(new RotateUnit(this.diagram._resizingAdorner, [this], [rotate.angle]), false);
            }
            const b = this.bounds(), sc = new Point(b.width / 2, b.height / 2);
            let deltaAngle, newPosition;
            if (center) {
                deltaAngle = angle - rotate.angle;
                newPosition = b.center().rotate(deltaAngle, center).minus(sc);
                this._rotationOffset = this._rotationOffset.plus(newPosition.minus(b.topLeft()));
                this.position(newPosition);
            }
            this.visual.rotate(angle, sc);
            this.options.rotation.angle = angle;
            if (this.diagram && this.diagram._connectorsAdorner) {
                this.diagram._connectorsAdorner.refresh();
            }
            this.refreshConnections();
            if (this.diagram) {
                this.diagram.trigger(ITEMROTATE, { item: this });
            }
        }
        return rotate;
    }
    connections(type) {
        const result = [];
        let i, j, con, cons, ctr;
        for (i = 0; i < this.connectors.length; i++) {
            ctr = this.connectors[i];
            cons = ctr.connections;
            for (j = 0, cons; j < cons.length; j++) {
                con = cons[j];
                if (type === 'out') {
                    const source = con.source();
                    if (source.shape && source.shape === this) {
                        result.push(con);
                    }
                }
                else if (type === 'in') {
                    const target = con.target();
                    if (target.shape && target.shape === this) {
                        result.push(con);
                    }
                }
                else {
                    result.push(con);
                }
            }
        }
        return result;
    }
    refreshConnections() {
        this.connections().forEach(function (connection) {
            connection.refresh();
        });
    }
    /**
     * Gets a connector of this shape either by the connector's supposed name or
     * via a Point in which case the closest connector will be returned.
     *
     * @param nameOrPoint The name of a Connector or a Point.
     * @returns {Connector}
     */
    getConnector(nameOrPoint) {
        let i, ctr;
        if (isString(nameOrPoint)) {
            nameOrPoint = nameOrPoint.toLocaleLowerCase();
            for (i = 0; i < this.connectors.length; i++) {
                ctr = this.connectors[i];
                if (ctr.options.name.toLocaleLowerCase() === nameOrPoint) {
                    return ctr;
                }
            }
        }
        else if (nameOrPoint instanceof Point) {
            return closestConnector(nameOrPoint, this.connectors);
        }
        else {
            return this.connectors.length ? this.connectors[0] : null;
        }
    }
    getPosition(side) {
        const b = this.bounds(), fnName = side.charAt(0).toLowerCase() + side.slice(1);
        if (isFunction(b[fnName])) {
            return this._transformPoint(b[fnName]());
        }
        return b.center();
    }
    redraw(options) {
        if (options) {
            let shapeOptions = this.options;
            let boundsChange;
            this.shapeVisual.redraw(this._visualOptions(options));
            if (this._diffNumericOptions(options, [WIDTH, HEIGHT, X, Y])) {
                this.bounds(new Rect(shapeOptions.x, shapeOptions.y, shapeOptions.width, shapeOptions.height));
                boundsChange = true;
            }
            if (options.connectors) {
                shapeOptions.connectors = options.connectors;
                this._updateConnectors();
            }
            shapeOptions = deepExtend(shapeOptions, options);
            if (options.rotation || boundsChange) {
                this._rotate();
            }
            if (shapeOptions.content) {
                this.content(shapeOptions.content);
            }
        }
    }
    _updateConnectors() {
        const connections = this.connections();
        this.connectors = [];
        this._createConnectors();
        let connection;
        let source;
        let target;
        for (let idx = 0; idx < connections.length; idx++) {
            connection = connections[idx];
            source = connection.source();
            target = connection.target();
            if (source.shape && source.shape === this) {
                connection.source(this.getConnector(source.options.name) || null);
            }
            else if (target.shape && target.shape === this) {
                connection.target(this.getConnector(target.options.name) || null);
            }
            connection.updateModel();
        }
    }
    _diffNumericOptions(options, fields) {
        return diffNumericOptions.call(this, options, fields);
    }
    _visualOptions(options) {
        return {
            data: options.path,
            source: options.source,
            hover: options.hover,
            fill: options.fill,
            stroke: options.stroke
        };
    }
    _triggerBoundsChange() {
        if (this.diagram) {
            this.diagram.trigger(ITEMBOUNDSCHANGE, { item: this, bounds: this._bounds.clone() }); // the trigger modifies the arguments internally.
        }
    }
    _transformPoint(point) {
        const rotate = this.rotate(), bounds = this.bounds(), tl = bounds.topLeft();
        if (rotate.angle) {
            point.rotate(rotate.angle, rotate.center().plus(tl));
        }
        return point;
    }
    _transformedBounds() {
        const bounds = this.bounds(), tl = bounds.topLeft(), br = bounds.bottomRight();
        return Rect.fromPoints(this.diagram.modelToView(tl), this.diagram.modelToView(br));
    }
    _rotatedBounds() {
        const bounds = this.bounds().rotatedBounds(this.rotate().angle), tl = bounds.topLeft(), br = bounds.bottomRight();
        return Rect.fromPoints(tl, br);
    }
    _rotate() {
        const rotation = this.options.rotation;
        if (rotation && rotation.angle) {
            this.rotate(rotation.angle);
        }
        this._rotationOffset = new Point();
    }
    _hover(value) {
        const options = this.options, hover = options.hover;
        let stroke = options.stroke, fill = options.fill;
        if (value && isDefined(hover.stroke)) {
            stroke = deepExtend({}, stroke, hover.stroke);
        }
        if (value && isDefined(hover.fill)) {
            fill = hover.fill;
        }
        this.shapeVisual.redraw({
            stroke: stroke,
            fill: fill
        });
        if (options.editable && options.editable.connect) {
            this.diagram._showConnectors(this, value);
        }
    }
    _hitTest(value) {
        if (this.visible()) {
            const bounds = this.bounds(), angle = this.rotate().angle;
            let rotatedPoint;
            if (value.isEmpty && !value.isEmpty()) { // rect selection
                return Intersect.rects(value, bounds, angle ? angle : 0);
            }
            else { // point
                rotatedPoint = value.clone().rotate(angle, bounds.center()); // cloning is important because rotate modifies the point inline.
                if (bounds.contains(rotatedPoint)) {
                    return this;
                }
            }
        }
    }
    toJSON() {
        return {
            shapeId: this.options.id
        };
    }
    createShapeVisual() {
        const options = this.options;
        const visualOptions = this._visualOptions(options);
        const visualTemplate = options.visual;
        const type = (options.type + '').toLocaleLowerCase();
        let shapeVisual;
        visualOptions.width = options.width;
        visualOptions.height = options.height;
        if (isFunction(visualTemplate)) { // custom template
            shapeVisual = visualTemplate.call(this, options);
        }
        else if (visualOptions.data) {
            shapeVisual = new Path(visualOptions);
            translateToOrigin(shapeVisual);
        }
        else if (type === 'rectangle') {
            shapeVisual = new Rectangle(visualOptions);
        }
        else if (type === 'circle') {
            shapeVisual = new Circle(visualOptions);
        }
        else if (type === 'text') {
            shapeVisual = new TextBlock(visualOptions);
        }
        else if (type === 'image') {
            shapeVisual = new Image(visualOptions);
        }
        else {
            shapeVisual = new Path(visualOptions);
        }
        this.shapeVisual = shapeVisual;
        this.visual.append(this.shapeVisual);
    }
}

export { Shape };
